import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Droplets, Filter, FlaskConical, Layers, Wind, Beaker,
  Recycle, Trash2, Factory, Workflow, Microscope, Leaf,
  Waves, Sparkles, Gauge, Activity, Sun, Wrench,
  ShieldCheck, Zap, Anchor, Search, Database,
  CloudRain, Cog, Sprout,
} from "lucide-react";
import { SOLUTIONS, SOLUTION_DEEP, TECH_LIBRARY, STANDARDS_BADGES } from "../lib/content";
import FAQ from "../components/FAQ";
import Breadcrumb from "../components/Breadcrumb";
import SEO, { faqLd, serviceLd, breadcrumbLd } from "../components/SEO";
import SupplierMarquee from "../components/hidroverse/SupplierMarquee";
import ServiceVideoStrip from "../components/hidroverse/ServiceVideoStrip";
import { suppliersFor } from "../lib/content/suppliers";

const fadeUp = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.2, 0.7, 0.2, 1] } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };

// Hero image per solution slug — single source of truth, exported
// so TechnologyDetail can render the same hero on tech sub-pages.
// Every URL appears nowhere else in the codebase.
export const SOLUTION_HERO = {
  purification: "/projects/thornybush/thornybush_clean_day.webp",
  pumps: "/mockups/sol_pumps.webp",
  harvesting: "/generated/hero-harvesting.webp",
  "pool-spa": "/mockups/sol_pool_spa.webp",
  wastewater: "/generated/hero-wastewater.webp",
  irrigation: "/generated/hero-irrigation.webp",
  monitoring: "/generated/hero-monitoring.webp",
  rentals: "/projects/thornybush/thornybush_11.webp",
  service: "/generated/hero-service.webp",
};
// Back-compat alias used inside this file.
const HERO = SOLUTION_HERO;

// ============================================================
// SOLUTION-LEVEL MARKETING — "Why this matters" + "Why HidroVerse"
// ============================================================
const SOL_MARKETING = {
  purification: {
    accent: "#1FB6C1",
    why: "Source water is seldom pure or ready to use without treatment. Boreholes can carry a wide range of contaminants — faecal coliforms and protozoan cysts such as Cryptosporidium and Giardia, alongside iron, manganese, fluoride, nitrate, and the calcium and magnesium hardness that scales every appliance downstream. Surface water adds turbidity, suspended solids and pathogen load that climbs sharply after rain; where the catchment touches mining or intensive agriculture, heavy metals such as lead, arsenic and cadmium enter the picture. Brackish aquifers add dissolved salts that no media bed will ever remove. We build specialised purification systems that meet SANS 241, designed against the same engineering standards the global water industry publishes — and the WHO drinking-water guidelines.",
    whyHV: [
      ["A SANAS-accredited feed-water analysis before any design", "Every quote is preceded by a full SANS 241 panel on the actual source — microbiology, heavy metals, hardness, TDS, turbidity, fluoride, nitrate, iron, manganese and the relevant organic indicators — through a SANAS-accredited laboratory. The design pack is sized against that result."],
      ["A documented multi-barrier treatment train", "Pre-treatment, primary separation, polish and disinfection — each stage selected and sized against the feed, with redundancy on critical components. The treatment philosophy follows the multi-barrier principle endorsed by WHO and embedded in SANS 241."],
      ["Routine SANAS-accredited compliance sampling", "Potable systems on the HidroVerse Care contract are sampled on a published schedule by a SANAS-accredited laboratory. Certificates are filed in the compliance pack and made available to the operator, GM or regulator on request."],
      ["End-to-end engineering accountability", "Survey, design, install, commission, sample, report — under one contract, one engineer-of-record and one published response SLA. A single point of accountability across the lifetime of the plant."],
    ],
  },
  wastewater: {
    accent: "#42954B",
    why: "Wastewater is where the site either protects its licence to operate or quietly loses it. A plant that backs up is a guest experience disaster. A plant that discharges out of spec is a directors' liability problem. We build wastewater systems that are reliable, discreet and reuse-ready by default.",
    whyHV: [
      ["Reliability as the brief", "Designed for graceful degradation — no single failure stops the site's water leaving the site."],
      ["Reuse first", "Treated effluent is an asset. Irrigation, dust-suppression and sub-potable reuse engineered into the design so raw-water draw drops and the licence breathes."],
      ["Discreet by default", "Architectural integration, low-NPSH blowers, odour control — built in, not bolted on. Guests never see, smell or hear the plant."],
      ["Documented compliance", "Every discharge sampled to the licence wording, every record audit-ready."],
    ],
  },
  pumps: {
    accent: "#0C2A40",
    why: "A pump on its own is a commodity. A pressure system is an engineered thing. On a lodge, an estate or a hospital, it is what the guest, the patient and the kitchen actually feel. A five-star shower is a pressure calculation before it is a plumbing decision.",
    whyHV: [
      ["Pumps as systems, not commodities", "We design pressure sets against your real demand profile — peak occupancy, irrigation overlap, fire reserve. Not against a catalogue page."],
      ["Whisper-rated by default", "≤55 dBA at 1 m on every standard VSD set. Guests do not hear the pump room. They hear the bushveld."],
      ["Redundancy engineered in", "Primary + standby + automatic fail-over in under 30 seconds, sized so the standby alone holds peak."],
      ["Critical-response clause, in writing", "When something does fail, the service agreement names a written on-site response window — measured at the alarm timestamp, with automatic service credits if missed."],
    ],
  },
  harvesting: {
    accent: "#1D6A8C",
    why: "Storage isn't a tank — it's three calculations: daily demand, peak demand and emergency reserve. Get any of them wrong and the site pays for it eight months later in a dry February. We size storage against real occupancy curves, not brochure numbers.",
    whyHV: [
      ["Yield-tested, not chart-sized", "Every borehole gets a step-test and a constant-discharge test. We size the pump to the proven yield, not the licence maximum."],
      ["Multi-source by default", "Rain carries first, borehole second, municipal third — or whatever the cost and quality stack dictates. Auto-blended, telemetered."],
      ["Drought-resilient sizing", "Storage sized against historical worst-drought rainfall, not the average. February-proof."],
      ["Architecturally discreet", "Tanks integrated into the site's design language — not afterthoughts on a slab."],
    ],
  },
  "pool-spa": {
    accent: "#1FB6C1",
    why: "Pool and spa water carries every guest's first impression. Cloudy pool = bad review. Chlorine smell = worse review. We engineer pool systems for improved maintenance economics, reduced life-cycle costs, and reliability in remote environments — without compromising the swim.",
    whyHV: [
      ["Crystal-clear without the chore", "Smarter media and smarter disinfection mean the lodge's team isn't taking pH readings every two hours."],
      ["Quiet and discreet", "The pool deck is a guest space. We design hydraulics and acoustics so the pool earns its photograph and the pump never appears in it."],
      ["Lifecycle, not sticker price", "Cheapest pump and cheapest sand cost the most over five years. We spec to the cycle cost."],
      ["International standards", "Hospitality chemistry, hygiene and safety designed against international standards — not just the local minimum."],
    ],
  },
  irrigation: {
    accent: "#42954B",
    why: "Irrigation is where you either steward water as an asset or waste it as a habit. In the bushveld, in the Karoo, in the citrus belt — water is too expensive to spray on the wrong leaf. We design irrigation that delivers the right water, to the right plant, at the right depth, at the right time.",
    whyHV: [
      ["Drip-first, by default", "Lowest water use, highest precision, fewest weed-promoting wet leaves."],
      ["Smart ET-zero control", "Each zone irrigates only when soil moisture and ET data say it must. Significant water savings, healthier plants."],
      ["Matched precipitation rates", "Sprinkler zones designed for uniform coverage — not for apparent flow."],
      ["Manager visibility", "Every zone logged. Every restriction respected. Every event reported."],
    ],
  },
  monitoring: {
    accent: "#0C2A40",
    why: "The plant you can't see is the plant you can't defend. By the time a leak is visible at the basin tap, it has been costing money for days. We instrument every install so the duty manager — and the engineer behind it — knows about anomalies before guests do.",
    whyHV: [
      ["Sees what matters", "Pressure, flow, level, conductivity, UV intensity. Logged at engineered intervals, alarmed on thresholds."],
      ["Reaches everywhere", "LoRaWAN for off-grid sensors, cellular for sites with coverage. Multi-kilometre range, multi-year battery life."],
      ["Audit-ready", "The data that drives the operator's dashboard is the data that defends the certificate."],
      ["Predictive, not reactive", "Trends spot creeping issues before they fail. Service becomes scheduled, not panicked."],
    ],
  },
  rentals: {
    accent: "#1D6A8C",
    why: "Some sites cannot wait for capital approval. Others prefer opex on the balance sheet over an asset they don't want to own. HidroVerse rents and leases the same equipment we install — under fixed monthly terms, with service, sampling and compliance included.",
    whyHV: [
      ["Bridge while capex moves", "Rent now, decide later. Most bridge contracts roll into a lease or a purchase once the board signs off."],
      ["Lease-to-own that converts cleanly", "Every Rand of lease payment converts toward purchase at any review point. No financial trap doors."],
      ["Service inside the fee", "Filter changes, UV lamps, sampling and callouts are all inside the monthly. No surprise invoices."],
      ["Same equipment, rental terms", "No second-tier rental fleet. The same plant we put on flagship lodges, on a rental contract."],
    ],
  },
  service: {
    accent: "#1D6A8C",
    why: "A water plant is a living thing. Membranes foul. UV lamps age. Resin exhausts. Without a service contract that genuinely supports the plant, every install becomes a future emergency. We sign every install we ship — and we stay accountable for the lifetime of it.",
    whyHV: [
      ["Critical-response clause, in writing", "Contracted critical-site response window, with pre-positioned spares on remote sites and service credits triggered against the alarm timestamp."],
      ["Monthly SANAS sampling", "Every potable system tested monthly at an accredited lab. Certificate filed. Compliance defended."],
      ["Preventive, not reactive", "Scheduled CIP, lamp swaps, dosing recalibration, electrical inspection — all on the calendar."],
      ["One supplier, lifetime cover", "We install it. We sample it. We service it. We sign it. Year on year."],
    ],
  },
};

// ============================================================
// PER-TECH MARKETING — what each technology DOES FOR YOU.
// Plain English first; specs are secondary.
// Icon + accent per technology.
// ============================================================
const TECH_DETAIL = {
  // PURIFICATION
  "reverse-osmosis": { icon: Filter, accent: "#1FB6C1", brag: "95–99%", bragLbl: "TDS reduction",
    plain: "Strips dissolved salts, metals and small organics from any feed. The technology behind turning brackish borehole or coastal water into water you'd happily drink." },
  ultrafiltration: { icon: Layers, accent: "#1FB6C1", brag: ">log 4", bragLbl: "Bacteria removal",
    plain: "A physical 0.01-micron barrier — if the membrane is intact, the water is safe. Provable on demand, can't fail silently. Belt-and-braces safety on every potable feed." },
  nanofiltration: { icon: FlaskConical, accent: "#1FB6C1", brag: "Softens", bragLbl: "Hardness · Colour",
    plain: "Removes hardness, colour and tannins without the energy cost of full RO. The right answer for tea-coloured river and dam water that needs polishing, not desalinating." },
  "lamella-clarifier": { icon: Workflow, accent: "#1FB6C1", brag: "5-20×", bragLbl: "Footprint efficiency",
    plain: "Strips silt, clay and biological load before it can shred a downstream membrane. Turns a 6-month membrane failure cycle into a 5-year membrane life." },
  "sand-separators": { icon: Filter, accent: "#1FB6C1", brag: "Zero", bragLbl: "Power · Chemicals",
    plain: "First-line mechanical removal of grit and sand. No chemistry, no power, no consumables. Cheap to install, indispensable in the field." },
  "media-filtration": { icon: Layers, accent: "#1FB6C1", brag: "Polish", bragLbl: "Taste · Odour",
    plain: "Multi-media beds and activated carbon polishing. Removes the chlorine, taste, odour and trace organics that separate 'safe' from 'noticeably better'." },
  "ion-exchange": { icon: Beaker, accent: "#1FB6C1", brag: "Targeted", bragLbl: "Ion removal",
    plain: "Selective ion swap on resin beads — softens hard water, strips nitrate, fluoride or arsenic where the feed water demands it. Surgical, not blunt." },
  "uv-disinfection": { icon: Zap, accent: "#1FB6C1", brag: "Zero", bragLbl: "Residual chemistry",
    plain: "Ultraviolet light kills bacteria, viruses and protozoa without adding anything to the water. Silent, residual-free, provable with logged intensity readings." },
  ozone: { icon: Sparkles, accent: "#1FB6C1", brag: "Polish", bragLbl: "Taste · Colour",
    plain: "Powerful on-site oxidiser. Disinfects and polishes taste, odour and colour. The polish step that separates good from outstanding." },
  chlorination: { icon: Droplets, accent: "#1FB6C1", brag: "Residual", bragLbl: "Distribution defence",
    plain: "Provides the measurable residual disinfection that protects water as it travels through pipes to taps. Layered with UV or ozone for defence-in-depth." },
  desalination: { icon: Anchor, accent: "#1FB6C1", brag: "Sea → Potable", bragLbl: "Coastal duty",
    plain: "Seawater and high-brackish RO. Where the source is salt and the only alternative is tankering, this is the engineering that supplies a coastal site." },
  // WASTEWATER
  "fat-grease-traps": { icon: Trash2, accent: "#42954B", brag: "First line", bragLbl: "FOG interception",
    plain: "Catches the fats, oils and greases from your kitchen before they wreck downstream blowers and biology. Cheap insurance against expensive failure." },
  "septic-tanks": { icon: Layers, accent: "#42954B", brag: "Primary", bragLbl: "Settlement",
    plain: "Quiet first stage where solids settle and partial digestion begins. The foundation of every decentralised wastewater plant." },
  "screen-filters": { icon: Filter, accent: "#42954B", brag: "Protects", bragLbl: "Every pump & membrane",
    plain: "Mechanical screens that stop wipes, grit and rags from murdering every downstream blower, pump and membrane. The most overlooked engineering on the plant." },
  "aerobic-biological": { icon: Wind, accent: "#42954B", brag: "85-95%", bragLbl: "BOD removal",
    plain: "Where the bacteria do the real work. Engineered tanks where aerobic biology consumes dissolved organic load. The workhorse of every modern wastewater train." },
  mbbr: { icon: Recycle, accent: "#42954B", brag: "Compact", bragLbl: "High biomass",
    plain: "Plastic carrier biofilm reactor — orders of magnitude more biomass per cubic metre than activated sludge. Small footprint, shock-load tolerant, low operator skill." },
  sbr: { icon: Cog, accent: "#42954B", brag: "Excellent", bragLbl: "N + P removal",
    plain: "Time-cycle activated sludge in one tank. Superb nitrogen and phosphorus removal, ideal for sites with variable load and strict discharge limits." },
  "activated-sludge": { icon: Factory, accent: "#42954B", brag: "Proven", bragLbl: "Every scale",
    plain: "The classic suspended-growth aerobic process. Used at every scale from village to metropolis. Cost-effective where space and operator capability allow." },
  "membrane-bioreactor": { icon: Microscope, accent: "#42954B", brag: "Reuse-ready", bragLbl: "Single stage",
    plain: "Activated sludge with UF membranes replacing the clarifier. Produces near-reuse-ready effluent in one tiny footprint. The right answer for water-reuse mandates." },
  "nutrient-reduction": { icon: Leaf, accent: "#42954B", brag: "Green Drop", bragLbl: "Tertiary polish",
    plain: "Phosphorus precipitation, nitrate denitrification, final polish filters. The tail end that meets the strictest discharge consents." },
  // POOL-SPA
  "afm-media": { icon: Sparkles, accent: "#1FB6C1", brag: "≈ 4 µm", bragLbl: "vs. sand ≈ 25 µm",
    plain: "Bio-resistant activated-glass filtration media that outperforms sand on clarity, footprint and backwash water. Material change with measurable economics." },
  "salt-chlorination": { icon: Waves, accent: "#1FB6C1", brag: "Zero", bragLbl: "Chemical handling",
    plain: "On-site generation of chlorine from common salt by electrolysis. No chemical handling, smoother chemistry, lower long-term running cost." },
  "vsd-pumps": { icon: Gauge, accent: "#1FB6C1", brag: "Up to -80%", bragLbl: "Energy",
    plain: "Pumps that modulate to demand rather than running at single speed. Quieter, more economical, gentler on the entire system." },
  // PUMPS
  "booster-sets": { icon: Gauge, accent: "#0C2A40", brag: "<30 s", bragLbl: "Auto fail-over",
    plain: "Multi-pump packages with auto-rotation and automatic fail-over. Eliminate single-point-of-failure on every critical pressure line." },
  "submersible-pumps": { icon: Anchor, accent: "#0C2A40", brag: "10-400 m", bragLbl: "Depth range",
    plain: "Multi-stage stainless pumps designed for full immersion. Sized against the borehole's real yield test — never against a generic chart." },
  "fire-pumps": { icon: ShieldCheck, accent: "#0C2A40", brag: "SANS 10090", bragLbl: "Certified",
    plain: "Statutory fire-suppression pump sets — designed, signed and certified to SANS 10090. Designed in from the architectural brief, not retro-fitted." },
  // IRRIGATION
  "drip-irrigation": { icon: Sprout, accent: "#42954B", brag: "85-95%", bragLbl: "Water efficiency",
    plain: "Water delivered directly to the root zone. Evaporation-minimised, weed-suppressed, plant-friendly. The most water-economic irrigation technology in the field." },
  "pop-up-sprinklers": { icon: CloudRain, accent: "#42954B", brag: "Matched", bragLbl: "Precipitation rate",
    plain: "Engineered turf coverage with uniform precipitation across irregular zones. The right answer for lawns, fairways and broad-area landscape." },
  "smart-controllers": { icon: Activity, accent: "#42954B", brag: "ET-zero", bragLbl: "Smart logic",
    plain: "Programmable controllers tied to soil-moisture and ET-zero data. Water when the plant needs it — not on a clock. Significant savings, healthier plants." },
  "micro-jet": { icon: Sprout, accent: "#42954B", brag: "20-200 L/h", bragLbl: "Flow range",
    plain: "Low-flow spray heads for shrubs, ornamentals and horticulture. Between drip and pop-up in flow and coverage." },
  // MONITORING
  "lorawan-telemetry": { icon: Activity, accent: "#0C2A40", brag: "2-15 km", bragLbl: "Wireless range",
    plain: "Low-power long-range wireless telemetry that reaches the places Wi-Fi will never go. Multi-kilometre range, multi-year battery life." },
  "telemetry-logging": { icon: Database, accent: "#0C2A40", brag: "Audit-grade", bragLbl: "Records",
    plain: "Every reading, every event, timestamped and archived. The data that drives the dashboard is the data that defends the certificate." },
  "pressure-zone-monitoring": { icon: Gauge, accent: "#0C2A40", brag: "Real-time", bragLbl: "Leak alerts",
    plain: "Pressure trends per zone. Leaks, blockages and demand surges all visible from one console — often before a guest notices." },
  // HARVESTING
  "borehole-intake": { icon: Anchor, accent: "#1D6A8C", brag: "Yield-tested", bragLbl: "Sustainable",
    plain: "Properly cased, headworks-built, step-tested abstraction. The engineering between hole-in-the-ground and water-as-an-asset." },
  "rainwater-capture": { icon: CloudRain, accent: "#1D6A8C", brag: "Engineered", bragLbl: "Catchment",
    plain: "Engineered catchment, first-flush diversion, properly screened storage. Rainwater treated as an asset, not a hobby project." },
  "surface-water-intake": { icon: Waves, accent: "#1D6A8C", brag: "Licensed", bragLbl: "Abstraction",
    plain: "River, dam and spring intakes with appropriate screening, settling and ecological flow management." },
  "coastal-intake": { icon: Anchor, accent: "#1D6A8C", brag: "Bio-fouling", bragLbl: "Resistant",
    plain: "Bio-fouling-resistant, sand-tolerant intake design. The front end of every seawater desalination plant." },
  "smart-blending": { icon: Workflow, accent: "#1D6A8C", brag: "Multi-source", bragLbl: "Auto blending",
    plain: "Multi-source blending with level telemetry and priority logic. The right source feeds the right duty at the right time." },
  "storage-partitioning": { icon: ShieldCheck, accent: "#1D6A8C", brag: "Triple-duty", bragLbl: "Fire · daily · peak",
    plain: "Reserves engineered for daily, peak and emergency duties separately. Not a single tank doing three jobs badly." },
  // SERVICE
  "preventive-maintenance": { icon: Wrench, accent: "#1D6A8C", brag: "Scheduled", bragLbl: "Quarterly",
    plain: "Scheduled service tailored to each install's actual equipment. Filter swaps, CIP, dosing recalibration, electrical inspection — on the calendar." },
  "critical-response": { icon: Zap, accent: "#1D6A8C", brag: "SLA", bragLbl: "Critical clause",
    plain: "Same-day phone response, contracted critical-site response window, pre-positioned spares. The contract that supports the plant, not just sells it." },
  "scheduled-sampling": { icon: FlaskConical, accent: "#1D6A8C", brag: "Monthly", bragLbl: "SANAS lab",
    plain: "Monthly micro and chemical sampling at accredited labs. Programmed, dispatched, reported, filed. The compliance file writes itself." },
  "membrane-cip": { icon: Search, accent: "#1D6A8C", brag: "Beyond warranty", bragLbl: "Membrane life",
    plain: "Pressure-decay testing and scheduled chemical cleaning that lets membranes outlive their warranty." },
  // SOLAR
  "robotic-cleaning": { icon: Sun, accent: "#1D6A8C", brag: "+15%", bragLbl: "Yield recovered",
    plain: "Autonomous robotic cleaning that maintains panel yield in dusty bushveld and rooftop environments. Off-peak, low water, no operator." },
};

// ============================================================
// PER-TECH PHOTO — every URL is unique site-wide.
// Real photography: Unsplash (commercial-use, no attribution) and
// on-disk install photos. No path appears twice in this file or
// in IndustryDetail OFFERING_PHOTO.
// ============================================================
const U = (id) => `https://images.unsplash.com/${id}?w=1200&q=80&auto=format&fit=crop`;

const TECH_PHOTO = {
  // PURIFICATION — RO and Lamella retain real install photos; rest use
  // AI-generated photo-realistic equipment renders (Gemini Nano Banana)
  // saved under /public/generated/.
  "reverse-osmosis":     "/projects/thornybush/thornybush_10.webp",
  ultrafiltration:       "/library/purification/purification_02.webp",
  nanofiltration:        "/library/purification/purification_03.webp",
  "lamella-clarifier":   "/projects/likweti/likweti_01.webp",
  "sand-separators":     "/generated/sand-separators.webp",
  "media-filtration":    "/generated/media-filtration-vessel.webp",
  "ion-exchange":        "/generated/ion-exchange.webp",
  "uv-disinfection":     "/generated/uv-disinfection-reactor.webp",
  ozone:                 "/generated/ozone-polishing-realistic.webp",
  chlorination:          "/generated/chlorination.webp",
  desalination:          "/generated/desalination.webp",
  // WASTEWATER — every tile is AI-generated equipment photography
  "fat-grease-traps":    "/generated/fat-grease-traps.webp",
  "septic-tanks":        "/generated/septic-tanks.webp",
  "screen-filters":      "/generated/screen-filters.webp",
  "aerobic-biological":  "/generated/aerobic-biological.webp",
  mbbr:                  "/generated/mbbr.webp",
  sbr:                   "/generated/sbr.webp",
  "activated-sludge":    "/generated/activated-sludge.webp",
  "membrane-bioreactor": "/generated/membrane-bioreactor.webp",
  "nutrient-reduction":  "/generated/nutrient-reduction.webp",
  // POOL-SPA
  "afm-media":           "/generated/afm-media.webp",
  "salt-chlorination":   "/generated/salt-chlorination.webp",
  // PUMPS — Realistic vertical multistage + submersible photography
  "vsd-pumps":           "/generated/vsd-pump-set.webp",
  "booster-sets":        "/generated/booster-set-vertical.webp",
  "submersible-pumps":   "/generated/submersible-borehole-pump-real.webp",
  "fire-pumps":          "/generated/fire-pumps.webp",
  // IRRIGATION
  "drip-irrigation":     "/generated/drip-irrigation.webp",
  "pop-up-sprinklers":   "/generated/pop-up-sprinklers.webp",
  "smart-controllers":   "/generated/smart-controllers.webp",
  "micro-jet":           "/generated/micro-jet.webp",
  // MONITORING
  "lorawan-telemetry":         "/generated/lorawan-telemetry.webp",
  "telemetry-logging":         "/generated/telemetry-logging.webp",
  "pressure-zone-monitoring":  "/generated/pressure-zone-monitoring.webp",
  // HARVESTING
  "borehole-intake":       "/generated/borehole-intake.webp",
  "rainwater-capture":     "/generated/rainwater-capture.webp",
  "surface-water-intake":  "/generated/surface-water-intake.webp",
  "coastal-intake":        "/generated/coastal-intake.webp",
  "smart-blending":        "/generated/smart-blending.webp",
  "storage-partitioning":  "/generated/storage-partitioning.webp",
  // SERVICE — keep one real Karingani photo, the rest AI-generated
  "preventive-maintenance": "/generated/preventive-maintenance.webp",
  "critical-response":      "/projects/karingani/karingani_03.webp",
  "scheduled-sampling":     "/generated/scheduled-sampling.webp",
  "membrane-cip":           "/generated/membrane-cip.webp",
  // SOLAR
  "robotic-cleaning":      "/generated/robotic-cleaning.webp",
};

const TECH_TITLE_TO_SLUG = {
  // Purification
  "Reverse Osmosis (RO)": "reverse-osmosis",
  "Ultrafiltration (UF)": "ultrafiltration",
  "Nanofiltration (NF)": "nanofiltration",
  "Lamella & Clarifier Systems": "lamella-clarifier",
  "Lamella & Conventional Clarifiers": "lamella-clarifier",
  "Sand Separators & Screens": "sand-separators",
  "Sand Separators & Self-Cleaning Screens": "sand-separators",
  "Superfiltration & Media": "media-filtration",
  "Multi-Media & Activated Carbon Filtration": "media-filtration",
  "Multi-media & cartridge filtration": "media-filtration",
  "Resins & Ion Exchange": "ion-exchange",
  "Ion Exchange (Softening & Selective Removal)": "ion-exchange",
  "Disinfection — UV, Ozone, Chlorination": "uv-disinfection",
  "Disinfection — UV, ozone, chlorination": "uv-disinfection",
  "UV Disinfection": "uv-disinfection",
  "UV disinfection": "uv-disinfection",
  "Ozone & Advanced Oxidation": "ozone",
  "Ozone polishing": "ozone",
  "Desalination": "desalination",
  // Wastewater
  "Fat traps & grease arrestors": "fat-grease-traps",
  "Septic tanks & settling chambers": "septic-tanks",
  "Screen filters & raw effluent separation": "screen-filters",
  "Aerobic & Biological Reactors": "aerobic-biological",
  "MBBR (Moving Bed Biofilm Reactor)": "mbbr",
  "SBR (Sequencing Batch Reactor)": "sbr",
  "Activated sludge & advanced aeration": "activated-sludge",
  "Ultrafiltration (UF) & Membrane Bioreactors (MBR)": "membrane-bioreactor",
  "Nutrient reduction & polishing": "nutrient-reduction",
  // Pool-Spa
  "AFM® (Activated Filter Media)": "afm-media",
  "Salt chlorination": "salt-chlorination",
  // "Hybrid low-chemical systems" intentionally removed — no dedicated
  // detail page exists, fallback photo is used instead so the pool-spa
  // grid avoids a duplicate uv-disinfection image.
  "VSD pump systems": "vsd-pumps",
  // Pumps
  "Variable Speed Drive (VSD) pumps": "vsd-pumps",
  "Booster sets — duty/standby/redundant": "booster-sets",
  "Submersible & borehole pumps": "submersible-pumps",
  "Fire-suppression sets": "fire-pumps",
  // Irrigation
  "Drip & sub-surface drip": "drip-irrigation",
  "Pop-up & gear-driven sprinklers": "pop-up-sprinklers",
  "Micro-jet & micro-spray": "micro-jet",
  "Smart zone automation": "smart-controllers",
  // Monitoring
  "LoRaWAN / cellular telemetry": "lorawan-telemetry",
  "Pump status & vibration monitoring": "telemetry-logging",
  "Pressure-zone alerting": "pressure-zone-monitoring",
  // Harvesting
  "Borehole intake & abstraction": "borehole-intake",
  "Rainwater capture systems": "rainwater-capture",
  "Surface-water intake": "surface-water-intake",
  "Coastal & saline intake": "coastal-intake",
  "Smart transfer & blending": "smart-blending",
  "Daily-use, peak-demand & emergency partitioning": "storage-partitioning",
  // Service
  "Preventive care & inspection schedules": "preventive-maintenance",
  "Critical response teams": "critical-response",
  "Scheduled SANS 241 sampling": "scheduled-sampling",
  "Membrane integrity & CIP programmes": "membrane-cip",
  // Solar
  "Autonomous robotic cleaning": "robotic-cleaning",
};

// ============================================================
// PHOTO FALLBACK by TITLE — descriptive tiles that don't map to
// a dedicated technology slug get a unique Unsplash photo. Every
// URL here appears nowhere else on the site.
// ============================================================
const TITLE_PHOTO_FALLBACK = {
  "Therapeutic & wellness features":         "/generated/therapeutic-jacuzzi.webp",
  "Automatic backwash & control":            "/generated/backwash-control-valve.webp",
  "Hybrid low-chemical systems":             "/mockups/eq_pool_dosing.webp",
  "Pool & spa circulators":                  "/generated/pool-circulator-pump.webp",
  "Flood control & dewatering":              "/generated/dewatering-sump-pump.webp",
  "Irrigation pump stations":                "/generated/irrigation-pump-station-clean.webp",
  "Smart automation & monitoring":           "/generated/pump-automation-scada.webp",
  "Pressure-managed feed":                   U("photo-1665930489997-e5e9d527ae39"), // green pipes manifold
  "Telemetry on every block":                "/mockups/eq_monitoring_gateway.webp",
  "Fire & thatch perimeter irrigation":      "/generated/thatch-perimeter-irrigation.webp",
  "Per-zone metering":                       U("photo-1774019883172-a89730a86500"), // industrial valve
  "UV intensity & lamp-life logging":        U("photo-1519455953755-af066f52f1a6"), // water splash
  "Compliance documentation":                "/mockups/eq_service_hands.webp",
  "Building/estate management integration":  U("photo-1697996069354-6578258b5ec9"), // aerial plant building
  "Architecturally integrated storage":      U("photo-1768779611359-c4bb38ba3c1e"), // tower/windmill silhouette
  "SLA-backed coverage":                     U("photo-1513828583688-c52646db42da"), // gray commercial machine
  "Remote monitoring & predictive alerts":   U("photo-1513828742140-ccaa28f3eda0"), // grey industrial equipment
  "Documented hand-overs & file management": "/mockups/eq_service_parts.webp",
  "Water-efficient delivery":                U("photo-1622322977885-8ee6d45ea016"), // sedimentation court
  "Off-grid hardened mechanics":             U("photo-1699791912502-84f9cb24cb01"), // large metal pump
  "Silent, guest-friendly operation":        "/mockups/eq_pumps_vsd.webp",
};

function techSlugFor(title) {
  if (TECH_TITLE_TO_SLUG[title]) return TECH_TITLE_TO_SLUG[title];
  const guess = title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return TECH_LIBRARY[guess] ? guess : null;
}

export default function SolutionDetail() {
  const { slug } = useParams();
  const s = SOLUTIONS.find((x) => x.slug === slug);
  const idx = SOLUTIONS.findIndex((x) => x.slug === slug);
  const next = SOLUTIONS[(idx + 1) % SOLUTIONS.length];
  const prev = SOLUTIONS[(idx - 1 + SOLUTIONS.length) % SOLUTIONS.length];

  if (!s) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center text-center px-6 pt-32">
        <div>
          <div className="eyebrow mb-4 text-black/50">404</div>
          <h1 className="font-display text-3xl md:text-5xl mb-6">Solution not found.</h1>
          <Link to="/services" className="hv-magnetic">Services index <span aria-hidden>→</span></Link>
        </div>
      </div>
    );
  }

  const heroImg = HERO[slug];
  const mk = SOL_MARKETING[slug] || { accent: "#1FB6C1", why: "", whyHV: [] };
  const accent = mk.accent;
  const buyerQuestions = (s.whatToLookFor || []).map(([q, a]) => [q, a]);
  const allFaqs = [...buyerQuestions, ...(s.faqs || [])];
  const deep = SOLUTION_DEEP[slug];

  const breadcrumbItems = [
    ["Home", "/"],
    ["Services", "/services"],
    [s.name, `/services/${slug}`],
  ];
  const combinedLd = {
    "@context": "https://schema.org",
    "@graph": [serviceLd(s.name, s.lede), faqLd(s.faqs || []), breadcrumbLd(breadcrumbItems)],
  };

  return (
    <div data-testid={`solution-page-${slug}`}>
      <SEO title={s.name} description={s.metaDesc} path={`/services/${slug}`} ldJson={combinedLd} />

      {/* HERO — brand-led, no spec strip, photo + lede + CTA */}
      <section className="eng-hero">
        {heroImg && (
          <div className="eh-bg" aria-hidden>
            <img src={heroImg} alt={s.name} data-testid={`solution-hero-img-${slug}`} loading="lazy" decoding="async" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </div>
        )}
        <div className="eh-veil" aria-hidden />
        <div className="eh-content">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.div variants={fadeUp} className="mb-4">
              <Breadcrumb items={breadcrumbItems} theme="overlay" testid="solution-breadcrumb" />
            </motion.div>
            <motion.h1 variants={fadeUp}>{s.name}</motion.h1>
            <motion.p variants={fadeUp} className="eh-lede">{s.lede}</motion.p>
            <motion.div variants={fadeUp} className="eh-actions">
              <Link to="/contact" className="hv-magnetic" data-testid="solution-cta-spec">Discuss your water needs <span aria-hidden>→</span></Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* WHY THIS MATTERS — tight bottom to flow into Technologies */}
      {mk.why && (
        <section className="bg-white px-6 md:px-12 lg:px-20 pt-14 md:pt-20 pb-8 md:pb-10" data-testid="why-this-matters">
          <div className="max-w-[1100px] mx-auto">
            <p className="font-light text-black" style={{ fontSize: "clamp(0.95rem, 1.1vw, 1.05rem)", fontWeight: 400, lineHeight: 1.65, color: "#1A1F25" }}>
              {mk.why}
            </p>
          </div>
        </section>
      )}

      {/* TECHNOLOGIES — hybrid light theme: warm white, navy headings, teal accents */}
      {deep && (
        <section className="px-6 md:px-12 lg:px-20 pt-10 md:pt-12 pb-14 md:pb-20" style={{ background: "#FAFAF7", color: "#0C2A40" }} data-testid="solution-technologies">
          <div className="max-w-[1500px] mx-auto">
            <div className="section-head">
              <div>
                <h2 className="sh-title" style={{ color: "#0C2A40" }}>
                  The technologies<br />
                  <span style={{ color: accent }}>we deploy.</span>
                </h2>
              </div>
            </div>

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={stagger}
              className="spec-grid"
            >
              {deep.technologies.map(([title, body], i) => {
                const techSlug = techSlugFor(title);
                const td = techSlug ? TECH_DETAIL[techSlug] : null;
                const Icon = td?.icon || Droplets;
                const cardAccent = td?.accent || accent;
                const photo = (techSlug && TECH_PHOTO[techSlug]) || TITLE_PHOTO_FALLBACK[title] || null;
                const Tile = ({ children }) =>
                  techSlug ? (
                    <Link to={`/services/${slug}/tech/${techSlug}`} className="outcome-tile light" style={{ "--ot-accent": cardAccent }} data-testid={`solution-tech-${i}`}>
                      {children}
                    </Link>
                  ) : (
                    <div className="outcome-tile light" style={{ "--ot-accent": cardAccent }} data-testid={`solution-tech-${i}`}>
                      {children}
                    </div>
                  );
                return (
                  <motion.div key={title} variants={fadeUp}>
                    <Tile>
                      <div className="ot-icon"><Icon /></div>
                      {td?.brag && (
                        <div className="ot-brag">
                          <span className="ot-brag-num">{td.brag}</span>
                          <span className="ot-brag-lbl">{td.bragLbl}</span>
                        </div>
                      )}
                      <div className="ot-title">{title}</div>
                      <p className="ot-lede">{td?.plain || body}</p>
                      {techSlug && (
                        <div className="ot-cta">
                          <span>Read the reference</span>
                          <span className="ot-cta-arrow" aria-hidden>→</span>
                        </div>
                      )}
                      {photo && (
                        <div className="ot-photo" aria-hidden>
                          <img src={photo} alt="" loading="lazy" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                        </div>
                      )}
                    </Tile>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>
      )}

      {/* WHY HIDROVERSE — 4 differentiators as marketing tiles */}
      {mk.whyHV?.length > 0 && (
        <section className="bg-white px-6 md:px-12 lg:px-20 py-14 md:py-20" data-testid="why-hidroverse">
          <div className="max-w-[1500px] mx-auto">
            <div className="section-head">
              <div>
                <h2 className="sh-title">
                  What you get<br />
                  <span style={{ color: accent }}>when signing with us.</span>
                </h2>
              </div>
            </div>
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={stagger}
              className="spec-grid cols-2"
            >
              {mk.whyHV.map(([t, b]) => (
                <motion.div key={t} variants={fadeUp}>
                  <div className="outcome-tile light" style={{ "--ot-accent": accent }}>
                    <div className="ot-title" style={{ fontSize: "clamp(1.15rem, 1.7vw, 1.35rem)" }}>{t}</div>
                    <p className="ot-lede">{b}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* BUYER'S GUIDE — removed per user request */}

      {/* STANDARDS */}
      <section className="bg-[#fbfbfa] py-10 md:py-12 px-6 md:px-12 lg:px-20 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
        <div className="max-w-[1500px] mx-auto flex flex-wrap items-center justify-between gap-6">
          <div className="eyebrow text-black/55" style={{ fontSize: "0.6rem", letterSpacing: "0.28em" }}>
            Designed to · Documented to · Defensible by
          </div>
          <div className="badge-strip">
            {STANDARDS_BADGES.map((b) => <span key={b} className="badge light">{b}</span>)}
          </div>
        </div>
      </section>

      {/* FAQ — Buyer's guide, light hybrid theme */}
      <FAQ faqs={allFaqs} />

      {/* SUPPLIER MARQUEE — only the brands relevant to this discipline */}
      {suppliersFor(slug).length > 0 && (
        <SupplierMarquee category={slug} tone="light" />
      )}

      {/* SERVICE VIDEO STRIP — field clips tagged with this service slug.
          Hides itself when no clips match. Locked to live below the
          technologies / why-us content, above the prev/next + CTA. */}
      <ServiceVideoStrip service={slug} />

      {/* PREV / NEXT */}
      <section className="px-6 md:px-12 lg:px-20 py-14" style={{ background: "#f0f2f2" }}>
        <div className="max-w-[1300px] mx-auto grid md:grid-cols-2 gap-4">
          <Link to={`/services/${prev.slug}`} className="p-6 md:p-8 bg-white border hover:border-[#1FB6C1] transition-colors" style={{ borderColor: "rgba(0,0,0,0.08)" }} data-testid="prev-solution">
            <div className="eyebrow text-black/50 mb-3">← Previous solution</div>
            <div className="font-industrial text-[1.15rem]">{prev.name}</div>
          </Link>
          <Link to={`/services/${next.slug}`} className="p-6 md:p-8 bg-white border hover:border-[#1FB6C1] transition-colors text-right" style={{ borderColor: "rgba(0,0,0,0.08)" }} data-testid="next-solution">
            <div className="eyebrow text-black/50 mb-3">Next solution →</div>
            <div className="font-industrial text-[1.15rem]">{next.name}</div>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-strip">
        <div className="csi">
          <div>
            <h3>Brief us on your water needs.</h3>
            <p className="csi-meta">
              For personalised assistance and solutions, contact our specialists.
            </p>
          </div>
          <Link to="/contact" className="hv-magnetic" style={{ justifyContent: "center" }}>
            Contact us <span aria-hidden>→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
